/**
 * Word / phrase lexicons distilled from Wikipedia's "Signs of AI writing",
 * cross-checked against the excess-vocabulary literature (Kobak et al. 2025,
 * Science Advances; Juzek & Ward, COLING 2025; Liang et al. 2024) and 2024–26
 * practitioner tell-lists.
 *
 * Everything here is matched case-insensitively on whole word/phrase
 * boundaries. Entries are grouped by the category they map to. Where a phrase
 * carries a fixed suggestion, it lives in PHRASE_NOTES.
 *
 * Kept deliberately curated: high-signal tells that a human editor would also
 * raise an eyebrow at, rather than every word that ever appears in AI output.
 */

/**
 * AI-favoured single words (the "high-density AI vocabulary" list).
 *
 * Curation rule: only words that are RARE in human prose but hugely
 * overrepresented in LLM text (excess ratios of 10–35x in the corpus studies)
 * qualify. Deliberately ABSENT despite appearing on popular tell-lists —
 * because they saturate ordinary human registers and would punish human
 * writers: crucial, robust (engineering/stats), leverage (business/finance),
 * landscape (ecology, "competitive landscape"), nuanced (criticism),
 * comprehensive (legal/academic), notably, potential, findings.
 */
export const AI_VOCAB: string[] = [
  'delve',
  'delves',
  'delving',
  'tapestry',
  'testament',
  'meticulous',
  'meticulously',
  'intricate',
  'intricacies',
  'interplay',
  'underscore',
  'underscores',
  'underscoring',
  'boast',
  'boasts',
  'boasting',
  'bolster',
  'bolstered',
  'bolstering',
  'garner',
  'garners',
  'garnered',
  'pivotal',
  'vibrant',
  'multifaceted',
  'realm',
  'realms',
  'showcasing',
  'showcase',
  'showcases',
  'holistic',
  'seamless',
  'seamlessly',
  'myriad',
  'plethora',
  'enduring',
  'burgeoning',
  'unwavering',
  'indelible',
  'harness',
  'harnessing',
  'elevate',
  'elevates',
  'elevating',
  'foster',
  'fostering',
  'cultivate',
  'cultivating',
  'encompass',
  'encompassing',
  'commendable',
  'noteworthy',
  'paramount',
  'quintessential',
  'juncture',
  // rare-but-unmistakable excess vocabulary (Kobak et al. 2025; Juzek & Ward
  // 2025 "focal words"; Liang et al. 2024 — 10–35x overrepresented vs human
  // baselines, yet uncommon enough in human prose to be safe per-hit flags)
  'advancements',
  'surpassing',
  'excels',
  'comprehending',
  // Latinate upgrades — LLMs pick the formal variant where humans write "use"
  'utilize',
  'utilizes',
  'utilizing',
  // marketing-skewed verbs/adjectives — heavily AI, rare in formal human prose
  // (deliberately avoids classic formal words like "profound"/"compelling"
  // that 19th-c. science essays legitimately use).
  'transformative',
  'revolutionize',
  'revolutionise',
  'revolutionizing',
  'revolutionary',
  'pioneering',
  'unprecedented',
  'empower',
  'empowers',
  'empowering',
  'empowered',
  'streamline',
  'streamlines',
  'streamlined',
  'streamlining',
  'curated',
  'resonate',
  'resonates',
  'resonated',
  'underpin',
  'underpins',
  'underpinned',
  'spearhead',
  'spearheaded',
  'game-changing',
  'actionable',
  'supercharge',
  'turbocharge',
];

/**
 * Emerging multiword tells observed in current AI-assisted prose. These are
 * deliberately low-confidence: a single occurrence is an editorial prompt,
 * while nearby independent tells can promote the surrounding paragraph.
 */
export const EMERGING_SLOP_PHRASES: string[] = [
  'the real bottleneck',
  'load-bearing',
];

/**
 * Multiword tells for "inflated significance / editorialising".
 * These carry the strongest signal in the article.
 */
export const SIGNIFICANCE_PHRASES: string[] = [
  // NOTE: copula-avoidance ("stands as a testament") and light-verb inflation
  // ("plays a pivotal role") are now detected STRUCTURALLY in structural.ts, so
  // they're intentionally gone from this list. What remains are fixed idioms.
  'underscores the importance',
  'highlights the importance',
  'reflects the broader',
  'reflects a broader',
  'part of a broader',
  'setting the stage for',
  'left an indelible mark',
  'leaving an indelible mark',
  'leaves an indelible mark',
  'rich tapestry',
  'rich cultural heritage',
  'rich history',
  'evolving landscape',
  'ever-evolving landscape',
  'ever-changing landscape',
  'changing landscape',
  'a shift in',
  'marks a turning point',
  'a key turning point',
  'deeply rooted in',
  'a lasting legacy',
  'enduring legacy',
  'stood the test of time',
  'a beacon of',
  'testament to the',
  'shaping the future',
  'shape the future',
  'the annals of history',
  'continues to captivate',
  'captured the hearts',
  'captures the essence',
  'a symbol of',
  'symbolizing the',
  // outline-like conclusion formulas (the "Despite… challenges" tell)
  'despite these challenges',
  'despite its challenges',
  'faces several challenges',
  'faces numerous challenges',
  'challenges and opportunities',
  'future outlook',
  'only time will tell',
  'one thing is clear',
  'one thing is certain',
  'the possibilities are endless',
  'the future is bright',
  'for generations to come',
  'now more than ever',
  'more than ever before',
  'continues to shape',
  'continues to inspire',
  'continues to evolve',
  'continues to grow',
  'is home to',
  'plays host to',
  // superficial-analysis connectives ("a testament to the" measures 163 ipm in
  // instruction-tuned LLM corpora vs 0 in the human reference corpus)
  'valuable insights',
  'pave the way',
  'paves the way',
  'paving the way',
  'paved the way',
];

/** Promotional puffery / travel-brochure & press-release language. */
export const PROMO_PHRASES: string[] = [
  'nestled in the heart of',
  'nestled in',
  'in the heart of',
  'a hidden gem',
  'hidden gem',
  'must-visit',
  'must-see',
  'breathtaking',
  'stunning natural beauty',
  'natural beauty',
  'picturesque',
  'state-of-the-art',
  'cutting-edge',
  'groundbreaking',
  'world-class',
  'renowned for',
  'renowned',
  'award-winning',
  'one-of-a-kind',
  'unparalleled',
  'unrivaled',
  'unrivalled',
  'rich cultural',
  'vibrant culture',
  'bustling',
  'a diverse array of',
  'diverse array',
  'wide array of',
  'a wide range of',
  'boasts a',
  'boasts an',
  'boasting a',
  'commitment to excellence',
  'commitment to',
  'dedicated to providing',
  'proud to',
  'we are proud',
  'game-changer',
  'game changer',
  'top-notch',
  'unforgettable experience',
  'immersive experience',
];

/** Marketing verbs used to dodge plain "is/are/has" copulatives. */
export const COPULA_VERBS: string[] = [
  'serves as',
  'stands as',
  'acts as',
  'functions as',
  'emerges as',
  'positions itself as',
  'represents a',
  'marks a',
  'ventured into',
];

/** Vague attribution & weasel wording. */
export const VAGUE_PHRASES: string[] = [
  'experts argue',
  'experts say',
  'experts believe',
  'experts agree',
  'many experts',
  'some experts',
  'critics argue',
  'critics say',
  'some critics',
  'observers have noted',
  'observers note',
  'observers say',
  'industry reports',
  'industry experts',
  'studies suggest',
  'studies show',
  'studies have shown',
  'research suggests',
  'research shows',
  'reports suggest',
  'it is widely regarded',
  'it is widely believed',
  'it is often said',
  'is often considered',
  'is considered one of the',
  'is widely considered',
  'is regarded as one of',
  'many believe',
  'some argue',
  'it is believed that',
  'according to sources',
  'sources say',
  'several sources',
  'various sources',
  'numerous sources',
  'a growing number of',
  'a number of',
  'countless',
];

/** Formulaic sentence-opening transitions. */
export const OPENING_CONJUNCTIONS: string[] = [
  'moreover',
  'furthermore',
  'additionally',
  // RLHF pushes enumerative openers hard (firstly +4,794% base→instruct)
  'firstly',
  'secondly',
  'thirdly',
  'in addition',
  'consequently',
  'notably',
  'importantly',
  'ultimately',
  'in conclusion',
  'in summary',
  'to summarize',
  'to sum up',
  'overall',
  'all in all',
  'in essence',
  'as a result',
  'that being said',
  'with that said',
  'on the other hand',
  'when it comes to',
  'in today’s world',
  "in today's world",
  'in the modern era',
  'in the world of',
];

/** Editorialising meta / chatbot "voice". */
export const META_PHRASES: string[] = [
  // NOTE: "it is important/critical/essential to note that …" throat-clearing is
  // now detected STRUCTURALLY (expletive-it + cop + importance-adj + cognition
  // verb) in structural.ts. What remains here are fixed chatbot idioms.
  'let us explore',
  "let's explore",
  "let's dive in",
  "let's dive into",
  "let's take a look",
  'let us delve',
  'in this article',
  'in this section',
  'in this guide',
  'this article will',
  'this guide will',
  'we will explore',
  'we will delve',
  'we will discuss',
  'as we navigate',
  'navigating the',
  'in the ever-changing world',
  'as an ai',
  'as a language model',
  'i cannot provide',
  'i do not have access',
  'as of my last',
  'my knowledge cutoff',
  'knowledge cutoff',
  'in the realm of',
  'when it comes to understanding',
  'unlock the',
  'unlock your',
  'take your',
  'to the next level',
  'the world of',
  'a myriad of',
  'from a to z',
  'the key takeaway',
  'key takeaways',
  'at the end of the day',
  'in an era of',
  'in an era where',
  'in an age of',
  'in an age where',
  'in a world where',
  'in a world increasingly',
  'at its core',
  'dive deeper',
  'a deep dive',
  'navigating the complexities',
  'the intersection of',
  'look no further',
  'say goodbye to',
  'say hello to',
  'buckle up',
  'without further ado',
  'that being said',
  'needless to say',
  // self-signposting fanfare + conversational-register tells (GPT-4o/5 era):
  // the model narrates its own rhetoric, promising a payoff it rarely delivers
  "here's the kicker",
  "here's the thing",
  "let's break it down",
  "let's be real",
  "let's be honest",
  'the honest answer is',
  'the bottom line is',
  'no fluff',
  "chef's kiss",
  'it goes without saying',
  'have you ever wondered',
  'fast-paced world',
];

/**
 * Suggestions attached to specific phrases (optional). Keyed by the lower-cased
 * phrase. Falls back to a category-level message when absent.
 */
export const PHRASE_NOTES: Record<string, string> = {
  'stands as a testament': 'Just say what it shows. Drop “stands as a testament”.',
  'serves as a testament': 'Say what it demonstrates directly.',
  'plays a vital role': 'Name the role. What does it actually do?',
  'plays a crucial role': 'Name the role concretely.',
  'plays a pivotal role': 'Name the role concretely.',
  'underscores the importance': 'State the point instead of gesturing at importance.',
  'rich tapestry': 'A cliché. Describe the actual variety.',
  'nestled in the heart of': 'Travel-brochure filler. Just give the location.',
  'in the heart of': 'Usually removable. Give the location plainly.',
  'boasts a': 'Prefer “has”. Marketing verbs read as promotional.',
  'it is important to note': 'If it matters, just state it. Drop the throat-clearing.',
  "it's important to note": 'If it matters, just state it.',
  'it is worth noting': 'Drop it and state the fact.',
  "it's worth noting": 'Drop it and state the fact.',
  'in conclusion': 'Wikipedia and most prose don’t need a signposted conclusion.',
  moreover: 'Formulaic transition. Often removable.',
  furthermore: 'Formulaic transition. Often removable.',
  additionally: 'Formulaic transition. Often removable.',
  delve: 'Overused by LLMs. Try “look at”, “examine”, or cut it.',
  tapestry: 'A signature AI cliché. Describe the thing directly.',
  testament: 'Say what it demonstrates instead.',
  meticulous: 'Often padding. Show the care rather than asserting it.',
  realm: 'Vague. Name the actual field.',
  showcasing: 'Prefer “showing” or just describe it.',
  vibrant: 'Puffery. Show it with detail instead.',
  utilize: 'Prefer “use”.',
  utilizes: 'Prefer “uses”.',
  utilizing: 'Prefer “using”.',
  advancements: 'Prefer “advances”, or name the specific progress.',
  "here's the kicker": 'Self-signposting fanfare. Delete it and state the thing.',
  "here's the thing": 'Self-signposting fanfare. Delete it and state the thing.',
  'valuable insights': 'Name the insight. “Valuable insights” asserts value without showing any.',
  'pave the way': 'Say what it actually enables.',
  'the real bottleneck': 'Often a canned reveal. Name the constraint directly and show the evidence for it.',
  'load-bearing': 'Metaphorical “load-bearing” has become AI shorthand. Name what the thing actually supports.',
};
